/**
 * Position translations utility
 * Translates football positions between English and Spanish
 */

export const positionTranslations = {
  es: {
    // Compound formats (with category prefix)
    'Attack - Centre-Forward': 'Ataque - Delantero Centro',
    'Attack - Left Winger': 'Ataque - Extremo Izquierdo',
    'Attack - Right Winger': 'Ataque - Extremo Derecho',
    'Attack - Second Striker': 'Ataque - Segundo Delantero',
    'Midfield - Attacking Midfield': 'Mediocampo - Mediapunta',
    'Midfield - Central Midfield': 'Mediocampo - Mediocentro',
    'Midfield - Defensive Midfield': 'Mediocampo - Pivote',
    'Midfield - Left Midfield': 'Mediocampo - Mediocentro Izquierdo',
    'Midfield - Right Midfield': 'Mediocampo - Mediocentro Derecho',
    'Defender - Centre-Back': 'Defensa - Central',
    'Defender - Left-Back': 'Defensa - Lateral Izquierdo',
    'Defender - Right-Back': 'Defensa - Lateral Derecho',
    'Defender - Sweeper': 'Defensa - Líbero',
    
    // Simple formats
    'Centre-Forward': 'Delantero Centro',
    'Left Winger': 'Extremo Izquierdo',
    'Right Winger': 'Extremo Derecho',
    'Second Striker': 'Segundo Delantero',
    'Attacking Midfield': 'Mediapunta',
    
    // Midfielders
    'Central Midfield': 'Mediocentro',
    'Defensive Midfield': 'Pivote',
    'Left Midfield': 'Mediocentro Izquierdo',
    'Right Midfield': 'Mediocentro Derecho',
    'Attacking Midfielder': 'Mediapunta',
    
    // Defenders
    'Centre-Back': 'Defensa Central',
    'Left-Back': 'Lateral Izquierdo',
    'Right-Back': 'Lateral Derecho',
    'Sweeper': 'Líbero',
    'Defender': 'Defensa',
    
    // Generic categories
    'Attack': 'Ataque',
    'Midfield': 'Mediocampo',
    
    // Goalkeeper
    'Goalkeeper': 'Portero',
    
    // Generic fallback
    'N/A': 'N/A'
  },
  en: {
    // Compound formats (with category prefix)
    'Ataque - Delantero Centro': 'Attack - Centre-Forward',
    'Ataque - Extremo Izquierdo': 'Attack - Left Winger',
    'Ataque - Extremo Derecho': 'Attack - Right Winger',
    'Ataque - Segundo Delantero': 'Attack - Second Striker',
    'Mediocampo - Mediapunta': 'Midfield - Attacking Midfield',
    'Mediocampo - Mediocentro': 'Midfield - Central Midfield',
    'Mediocampo - Pivote': 'Midfield - Defensive Midfield',
    'Mediocampo - Mediocentro Izquierdo': 'Midfield - Left Midfield',
    'Mediocampo - Mediocentro Derecho': 'Midfield - Right Midfield',
    'Defensa - Central': 'Defender - Centre-Back',
    'Defensa - Lateral Izquierdo': 'Defender - Left-Back',
    'Defensa - Lateral Derecho': 'Defender - Right-Back',
    'Defensa - Líbero': 'Defender - Sweeper',
    
    // Simple formats
    'Delantero Centro': 'Centre-Forward',
    'Extremo Izquierdo': 'Left Winger',
    'Extremo Derecho': 'Right Winger',
    'Segundo Delantero': 'Second Striker',
    'Mediapunta': 'Attacking Midfield',
    
    // Midfielders
    'Mediocentro': 'Central Midfield',
    'Pivote': 'Defensive Midfield',
    'Mediocentro Izquierdo': 'Left Midfield',
    'Mediocentro Derecho': 'Right Midfield',
    'Centrocampista': 'Midfielder',
    
    // Defenders
    'Defensa Central': 'Centre-Back',
    'Lateral Izquierdo': 'Left-Back',
    'Lateral Derecho': 'Right-Back',
    'Líbero': 'Sweeper',
    'Defensa': 'Defender',
    
    // Generic categories
    'Ataque': 'Attack',
    'Mediocampo': 'Midfield',
    
    // Goalkeeper
    'Portero': 'Goalkeeper',
    
    // Generic fallback
    'N/A': 'N/A'
  }
};

/**
 * Translates a position from one language to another
 * @param position - The position to translate
 * @param targetLang - The target language ('es' or 'en')
 * @returns The translated position or the original if no translation found
 */
export const translatePosition = (position: string, targetLang: 'es' | 'en'): string => {
  if (!position || position === 'N/A') return position;
  
  // Get the translation map for the target language
  const translations = positionTranslations[targetLang];
  
  // Try exact match first
  if (translations[position as keyof typeof translations]) {
    return translations[position as keyof typeof translations];
  }
  
  // Try case-insensitive match
  const lowerPosition = position.toLowerCase();
  const matchedKey = Object.keys(translations).find(
    key => key.toLowerCase() === lowerPosition
  );
  
  if (matchedKey) {
    return translations[matchedKey as keyof typeof translations];
  }
  
  // Return original if no translation found
  return position;
};

/**
 * Translates foot preference
 * @param foot - The foot preference
 * @param targetLang - The target language ('es' or 'en')
 * @returns The translated foot preference
 */
export const translateFoot = (foot: string, targetLang: 'es' | 'en'): string => {
  const footTranslations = {
    es: {
      'right': 'Derecho',
      'left': 'Izquierdo',
      'both': 'Ambos'
    },
    en: {
      'derecho': 'Right',
      'izquierdo': 'Left',
      'ambos': 'Both'
    }
  };
  
  const translations = footTranslations[targetLang];
  const lowerFoot = foot.toLowerCase();
  
  if (translations[lowerFoot as keyof typeof translations]) {
    return translations[lowerFoot as keyof typeof translations];
  }
  
  return foot;
};
